import React, { useEffect } from 'react';
import useScrollLock from '../hooks/useScrollLock';
import { createPortal } from 'react-dom';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  // Prevent scrolling when modal is open
  useScrollLock(isOpen);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative bg-white/90 backdrop-blur-md rounded-3xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border border-orange-200/50 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex justify-between items-center p-6 sm:p-8 border-b border-orange-200/30 shrink-0">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Privacy Policy</h2>
            <p className="text-gray-500 text-sm mt-1">Last updated: March 1, 2026</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-900 text-2xl font-bold transition-colors w-10 h-10 flex items-center justify-center rounded-full hover:bg-black/5"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="overflow-y-auto p-6 sm:p-8 space-y-8">
          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Information We Collect</h3>
            <div className="space-y-3 text-gray-600">
              <p>We collect information you provide directly to us, such as when you create an account, use our services, or contact us for support.</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-gray-800">Account Information:</strong> Name, email address, and profile information</li>
                <li><strong className="text-gray-800">Focus Session Data:</strong> Session duration, goals, notes, and productivity metrics</li>
                <li><strong className="text-gray-800">Team Collaboration Data:</strong> Team member information, shared content, and communication logs</li>
                <li><strong className="text-gray-800">Usage Analytics:</strong> How you interact with our application and features</li>
                <li><strong className="text-gray-800">Device Information:</strong> Device type, operating system, and browser information</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">2. How We Use Your Information</h3>
            <div className="space-y-3 text-gray-600">
              <p>We use the information we collect to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Send technical notices, updates, and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Analyze usage patterns to improve user experience</li>
                <li>Detect, prevent, and address technical issues</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">3. Information Sharing</h3>
            <div className="space-y-3 text-gray-600">
              <p>We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>With your explicit consent</li>
                <li>To comply with legal obligations</li>
                <li>To protect our rights and prevent fraud</li>
                <li>With service providers who assist in our operations (under strict confidentiality agreements)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">4. Data Security</h3>
            <div className="space-y-3 text-gray-600">
              <p>We implement appropriate security measures to protect your personal information:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security audits and updates</li>
                <li>Access controls and authentication</li>
                <li>Secure data centers with physical security</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">5. Your Rights</h3>
            <div className="space-y-3 text-gray-600">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Delete your account and associated data</li>
                <li>Export your data in a portable format</li>
                <li>Opt out of certain data processing</li>
                <li>Withdraw consent where applicable</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">6. Cookies and Tracking</h3>
            <div className="space-y-3 text-gray-600">
              <p>We use cookies and similar technologies to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Remember your preferences and settings</li>
                <li>Analyze how you use our services</li>
                <li>Improve performance and user experience</li>
                <li>Provide personalized content</li>
              </ul>
              <p>You can control cookie settings through your browser preferences.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">7. Data Retention</h3>
            <div className="space-y-3 text-gray-600">
              <p>We retain your information for as long as necessary to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide our services to you</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes and enforce agreements</li>
                <li>Improve our services</li>
              </ul>
              <p>When you delete your account, we will delete your personal information within 30 days, unless we are required to retain it for legal reasons.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-semibold text-gray-900 mb-3">8. Contact Us</h3>
            <div className="space-y-3 text-gray-600">
              <p>If you have any questions about this privacy policy or our data practices, please contact us:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Email: support@focuzon.com</li>
              </ul>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-orange-200/30 bg-orange-50/50 rounded-b-3xl">
          <p className="text-center text-gray-500 text-sm">
            By using our services, you agree to the collection and use of information in accordance with this policy.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PrivacyPolicyModal;
